import { Box, Link, Typography } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { formatNumberToCurrency } from '@/utils/currency';

interface SubcategoryGroupHeaderProps {
  name: string;
  /** Accounts in the group that pass the active filter. */
  count: number;
  /** Their balances, totalled. */
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Group header for a mobile list view, where the single list column is the whole
 * row and so has to draw the name, the expand toggle and the subtotal itself.
 *
 * Shared by the balance sheet drill-downs on both surfaces so their group rows
 * cannot drift apart.
 */
export default function SubcategoryGroupHeader({
  name,
  count,
  total,
  isExpanded,
  onToggle,
}: SubcategoryGroupHeaderProps) {
  return (
    <Box
      sx={{
        width: '100%',
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Link
        component='button'
        type='button'
        aria-expanded={isExpanded}
        underline='none'
        color='text.primary'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          textAlign: 'left',
          fontWeight: 'bold',
        }}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
      >
        {isExpanded ? (
          <ExpandLess fontSize='small' />
        ) : (
          <ExpandMore fontSize='small' />
        )}
        <Box
          component='span'
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {name} ({count})
        </Box>
      </Link>
      <Typography
        variant='body2'
        sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
      >
        {formatNumberToCurrency(total)}
      </Typography>
    </Box>
  );
}
